import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import { RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PendingItem } from '../../services/d5m2Api';
import { useD5M2Reversal } from './hooks/useD5M2Reversal';
import { d5m2Api } from '../../services/d5m2Api';
import {
  BatchAcceptanceBridge,
  pwd5m2f1AcceptanceManifest,
} from '../../features/acceptance';
import type { AcceptanceManifest } from '../../features/acceptance';

const { Title, Text } = Typography;
const acceptanceActionIds = {
  list: 'PWD5N2F1-ACT-QUERY',
  writeoff: 'PWD5N2F1-ACT-REVERSE',
};
void acceptanceActionIds;

// ============================================================
// Helpers
// ============================================================

function renderStatus(status: string): React.ReactNode {
  if (!status || status === '') return <Tag color="default">未送審</Tag>;
  if (status === 'P') return <Tag color="blue">已核准</Tag>;
  if (status === 'N') return <Tag color="red">已沖銷</Tag>;
  return <Tag color="orange">{status}</Tag>;
}

// ============================================================
// Main Component
// ============================================================

interface D5M2ReversalProps {
  api?: typeof d5m2Api;
  manifest?: AcceptanceManifest;
}

const D5M2Reversal: React.FC<D5M2ReversalProps> = ({
  api = d5m2Api,
  manifest = pwd5m2f1AcceptanceManifest,
}) => {
  const activeManifest = manifest;
  const {
    loading,
    reversing,
    pendingItems,
    total,
    selectedKeys,
    filter,
    lastApiResponse,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchReverse,
  } = useD5M2Reversal(api);

  // ============================================================
  // Table columns
  // ============================================================
  const columns: ColumnsType<PendingItem> = [
    {
      title: <span data-contract-field="fd120_01">繳庫單號</span>,
      dataIndex: 'doc_no',
      key: 'doc_no',
      width: 100,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: <span data-contract-field="f104_12">供應商名稱</span>,
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      ellipsis: true,
    },
    {
      title: <span data-contract-field="fd120_12">供應商代碼</span>,
      dataIndex: 'vendor_code',
      key: 'vendor_code',
      width: 110,
    },
    {
      title: <span data-contract-field="f106_11">部門</span>,
      dataIndex: 'dept_name',
      key: 'dept_name',
      width: 100,
    },
    {
      title: <span data-contract-field="fd120_11">繳庫日期</span>,
      dataIndex: 'receive_date',
      key: 'receive_date',
      width: 110,
      render: (date: string | null) =>
        date ? new Date(date).toLocaleDateString('zh-TW') : '—',
    },
    {
      title: <span data-contract-field="fd120_90">版本</span>,
      dataIndex: 'version_count',
      key: 'version_count',
      width: 60,
      align: 'center',
    },
    {
      title: <span data-contract-field="fd120_80">狀態</span>,
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: renderStatus,
    },
  ];

  // ============================================================
  // Row selection
  // ============================================================
  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  // ============================================================
  // Filter form handlers
  // ============================================================
  const handleSearch = () => { fetchPending(); };

  const handleFilterChange = (field: 'docStart' | 'docEnd', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  // Auto-fill docEnd when docStart is entered (BR-U02 equivalent)
  const handleDocStartBlur = () => {
    if (filter.docStart && !filter.docEnd) {
      const autoEnd = (filter.docStart + 'zzzzzzzz').substring(0, 8);
      setFilter({ ...filter, docEnd: autoEnd });
    }
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginBottom: 16 }}>繳庫單核准沖銷 (pwd5m2f1)</Title>

      {/* Filter Bar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label={<span data-contract-field="fd120_01">單號起始</span>}>
            <Input
              value={filter.docStart}
              onChange={(e) => handleFilterChange('docStart', e.target.value)}
              onBlur={handleDocStartBlur}
              placeholder="例：FD240001"
              style={{ width: 120 }}
              maxLength={8}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label={<span data-contract-field="fd120_01">單號結束</span>}>
            <Input
              value={filter.docEnd}
              onChange={(e) => handleFilterChange('docEnd', e.target.value)}
              placeholder="例：FD240099"
              style={{ width: 120 }}
              maxLength={8}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <span data-acceptance-action="PWD5N2F1-ACT-QUERY">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
              data-acceptance-action="PWD5M2-ACT-QUERY"
            >
              查詢
            </Button>
            </span>
          </Form.Item>
        </Form>
      </Card>

      {/* Summary & Actions */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{total}</strong> 筆待沖銷
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`確認沖銷 ${selectedKeys.length} 筆繳庫單？沖銷後狀態將設為 N（初始），並建立版本快照，無法還原。`}
              onConfirm={batchReverse}
              okText="確認沖銷"
              cancelText="取消"
              disabled={selectedKeys.length === 0}
              okButtonProps={{ danger: true }}
            >
              <span data-acceptance-action="PWD5N2F1-ACT-REVERSE">
              <Button
                danger
                icon={<RollbackOutlined />}
                loading={reversing}
                disabled={selectedKeys.length === 0}
                data-acceptance-action="PWD5M2-ACT-REVERSE"
              >
                批量沖銷
              </Button>
              </span>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      {/* Data Table */}
      <Table<PendingItem>
        rowKey="doc_no"
        columns={columns}
        dataSource={pendingItems}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        scroll={{ x: 750 }}
        locale={{ emptyText: '無待沖銷資料' }}
      />
      <BatchAcceptanceBridge
        manifest={activeManifest}
        records={pendingItems}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.doc_no}
        queryExact={async (key) => {
          const result = await api.getPending({ docStart: key, docEnd: key });
          return result.items?.[0] || null;
        }}
        lastApiResponse={lastApiResponse}
        loading={loading || reversing}
        onAutoFill={() => {
          const first = pendingItems[0];
          if (first) setSelectedKeys([first.doc_no]);
        }}
        autoFillDisabled={pendingItems.length === 0}
        autoFillReason="只選取正式 API 回傳的第一筆已核准繳庫單，不直接執行退出。"
        screenDraft={{ filter, selected_items: selectedKeys }}
      />
    </div>
  );
};

export default D5M2Reversal;
